/**
 * Governs whether a resolved error's raw `detail` may be interpolated into the
 * user-facing translated message (D1 — ADR-3).
 *
 * Default-deny (R4): a key not in `DETAIL_SAFE_KEYS` is opaque to the user, by
 * construction — `internal.downstream_service.error` and every unmapped/
 * `ContractViolation` case included. This gates the rendered toast/message ONLY:
 * `detail` is always forwarded to Sentry via `reporting.ts` regardless of this policy,
 * and is always visible in the Network tab.
 *
 * Starts EMPTY (design risk #3): every key is currently opaque to the user, which is
 * the safe default. Populate per-key only once that key's `detail` shape is verified
 * against a real payload AND the translated copy for that key has a `{{detail}}`
 * placeholder to receive it.
 */
export const DETAIL_SAFE_KEYS: ReadonlySet<string> = new Set([]);

export const isDetailUserVisible = (key: string | undefined): boolean =>
	typeof key === 'string' && DETAIL_SAFE_KEYS.has(key);

/**
 * Only a non-empty string `detail` is ever eligible for interpolation — object/array
 * details (bulk, validation) are never interpolated even for an allow-listed key.
 */
export const asDetailText = (detail: unknown): string | undefined =>
	typeof detail === 'string' && detail.length > 0 ? detail : undefined;
